/**
 * Landing gear builder — simplified struts and wheels.
 * Tricycle configuration: nose gear + two main gear.
 */

import * as THREE from 'three'

const STRUT_SEGMENTS = 4
const WHEEL_SEGMENTS = 8

export function createLandingGear(
  fuselageRadius: number,
  fuselageLength: number,
  _wingspan: number,
  material: THREE.MeshStandardMaterial
): THREE.Group {
  const group = new THREE.Group()
  group.name = 'landing-gear'

  const strutRadius = fuselageRadius * 0.04
  const strutHeight = fuselageRadius * 0.8
  const wheelRadius = fuselageRadius * 0.12
  const wheelWidth = wheelRadius * 0.4

  // Nose gear — single strut + wheel under nose
  // Position struts from fuselage bottom (Y = -fuselageRadius), not centerline (Y = 0)
  const noseZ = fuselageLength * 0.35
  const noseStrut = createStrut(strutRadius, strutHeight, material)
  noseStrut.position.set(0, -fuselageRadius - strutHeight / 2, noseZ)
  group.add(noseStrut)

  const noseWheel = createWheel(wheelRadius, wheelWidth, material)
  noseWheel.position.set(0, -fuselageRadius - strutHeight, noseZ)
  group.add(noseWheel)

  // Main gear — two struts + wheels at ~40% from rear
  const mainZ = -fuselageLength * 0.10
  const mainX = fuselageRadius * 0.8

  for (const side of [-1, 1] as const) {
    const mainStrut = createStrut(strutRadius, strutHeight, material)
    mainStrut.position.set(side * mainX, -fuselageRadius - strutHeight / 2, mainZ)
    group.add(mainStrut)

    const mainWheel = createWheel(wheelRadius, wheelWidth, material)
    mainWheel.position.set(side * mainX, -fuselageRadius - strutHeight, mainZ)
    group.add(mainWheel)
  }

  return group
}

function createStrut(
  radius: number,
  height: number,
  material: THREE.MeshStandardMaterial
): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(radius, radius, height, STRUT_SEGMENTS)
  const mesh = new THREE.Mesh(geo, material)
  mesh.castShadow = true
  return mesh
}

function createWheel(
  radius: number,
  width: number,
  material: THREE.MeshStandardMaterial
): THREE.Mesh {
  // Torus for wheel shape (simplified)
  const geo = new THREE.CylinderGeometry(radius, radius, width, WHEEL_SEGMENTS)
  const mesh = new THREE.Mesh(geo, material)
  // Rotate so wheel sits flat on ground with axle along X
  mesh.rotation.z = Math.PI / 2
  mesh.castShadow = true
  return mesh
}
